import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ThumbsUpIcon } from 'lucide-react';
interface SwipeTestProps {
  onComplete: (correct: number, total: number, timeSeconds: number) => void;
}
type CardType = 'google-5star' | 'google-1star' | 'imessage-good' | 'imessage-bad' | 'lead' | 'alert';
type Decision = 'keep' | 'toss';
interface ScenarioCard {
  id: string;
  type: CardType;
  content: string;
  author?: string;
  rating?: number;
  time?: string;
  correctAnswer: Decision;
  label?: string;
}
const ALL_SCENARIOS: ScenarioCard[] = [{
  id: 'g5-1',
  type: 'google-5star',
  rating: 5,
  author: 'Sarah M.',
  content: 'Absolutely incredible experience from start to finish. The team was professional, fast, and genuinely cared about getting it right. Will 100% be back and telling everyone I know!',
  correctAnswer: 'keep'
}, {
  id: 'g5-2',
  type: 'google-5star',
  rating: 5,
  author: 'James T.',
  content: "Best decision I ever made. Ordered online, arrived in 2 days, exactly as described. Customer service actually picked up the phone. Rare these days. Five stars isn't enough.",
  correctAnswer: 'keep'
}, {
  id: 'g5-3',
  type: 'google-5star',
  rating: 5,
  author: 'Priya K.',
  content: 'Booked a last-minute appointment and they fit me in same day. Friendly, efficient, and the results speak for themselves. My go-to from now on.',
  correctAnswer: 'keep'
}, {
  id: 'g5-4',
  type: 'google-5star',
  rating: 5,
  author: 'Marcus D.',
  content: "I've tried a lot of options in this space. Nothing comes close. The quality is unmatched and the team actually follows up to make sure you're happy. Highly recommend.",
  correctAnswer: 'keep'
}, {
  id: 'g1-1',
  type: 'google-1star',
  rating: 1,
  author: 'Angry Customer',
  content: 'Worst experience of my life. Waited over an hour, nobody apologized, and the result was terrible. Complete waste of money. Zero stars if I could. AVOID.',
  correctAnswer: 'toss'
}, {
  id: 'g1-2',
  type: 'google-1star',
  rating: 1,
  author: 'Disappointed Buyer',
  content: 'Item arrived broken. Reached out 3 times and got zero response. Had to dispute with my bank. This business does not care about its customers at all.',
  correctAnswer: 'toss'
}, {
  id: 'g1-3',
  type: 'google-1star',
  rating: 1,
  author: 'Frustrated Client',
  content: "They cancelled my appointment 30 minutes before with no explanation and never rescheduled. Completely unprofessional. I'll be taking my business elsewhere.",
  correctAnswer: 'toss'
}, {
  id: 'im-good-1',
  type: 'imessage-good',
  label: 'Team Chat',
  author: 'Alex',
  content: "Hey, I stayed 2 hours past close to get the job done. It's done. Thank God! 🙏",
  time: '9:47 PM',
  correctAnswer: 'keep'
}, {
  id: 'im-good-2',
  type: 'imessage-good',
  label: 'Team Chat',
  author: 'Jordan',
  content: "Just finished packing the last order. Everything's labeled and ready to ship first thing tomorrow. You're all set! 💪",
  time: '8:12 PM',
  correctAnswer: 'keep'
}, {
  id: 'im-good-3',
  type: 'imessage-good',
  label: 'Team Chat',
  author: 'Taylor',
  content: 'Customer was upset when they came in. I comped their service and they left happy. Left us a 5-star review already 😊',
  time: '3:30 PM',
  correctAnswer: 'keep'
}, {
  id: 'im-bad-1',
  type: 'imessage-bad',
  label: 'Team Chat',
  author: 'Chris',
  content: "I have a headache. I'm going home.",
  time: '1:15 PM',
  correctAnswer: 'toss'
}, {
  id: 'im-bad-2',
  type: 'imessage-bad',
  label: 'Team Chat',
  author: 'Riley',
  content: "Can someone cover my shift? I just don't feel like coming in today lol",
  time: '7:02 AM',
  correctAnswer: 'toss'
}, {
  id: 'im-bad-3',
  type: 'imessage-bad',
  label: 'Team Chat',
  author: 'Morgan',
  content: 'Oops. I think I accidentally sent the wrong order to like 12 customers. Should I tell someone? 😬',
  time: '2:44 PM',
  correctAnswer: 'toss'
}, {
  id: 'lead-1',
  type: 'lead',
  label: 'New Lead',
  author: 'Website Form',
  content: "Hi! I saw your ad and I'm really interested in what you offer. How do I get started? I'm ready to move forward.",
  time: 'Just now',
  correctAnswer: 'keep'
}, {
  id: 'lead-2',
  type: 'lead',
  label: 'New Lead',
  author: 'Instagram DM',
  content: "Omg I've been following you for months and I finally want to book. What's your availability like this week?",
  time: '2 min ago',
  correctAnswer: 'keep'
}, {
  id: 'lead-3',
  type: 'lead',
  label: 'New Lead',
  author: 'Referral',
  content: "My friend used your service and won't stop talking about it. I need to try this. Do you have any openings?",
  time: '5 min ago',
  correctAnswer: 'keep'
}, {
  id: 'alert-1',
  type: 'alert',
  label: 'Business Alert',
  content: 'A local news outlet wants to feature your business in an upcoming story about top local brands.',
  correctAnswer: 'keep'
}, {
  id: 'alert-2',
  type: 'alert',
  label: 'Business Alert',
  content: 'You just hit your monthly revenue goal — 3 weeks early. 🎉',
  correctAnswer: 'keep'
}, {
  id: 'alert-3',
  type: 'alert',
  label: 'Business Alert',
  content: 'Your website went down during your biggest sale of the year. Customers are getting error pages.',
  correctAnswer: 'toss'
}, {
  id: 'alert-4',
  type: 'alert',
  label: 'Business Alert',
  content: 'You just received a chargeback on your largest order this month. Reason: "Item not as described."',
  correctAnswer: 'toss'
}, {
  id: 'alert-5',
  type: 'alert',
  label: 'Business Alert',
  content: 'Your top-performing employee just asked for a raise after their best quarter yet.',
  correctAnswer: 'keep'
}];
const CARDS_PER_GAME = 8;
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function GoogleStars({
  rating
}: {
  rating: number;
}) {
  return <div className="flex gap-0.5" data-id="element-133">
      {[1, 2, 3, 4, 5].map(i => <svg key={i} className={`w-4 h-4 ${i <= rating ? 'text-[#FBBC04]' : 'text-gray-300'}`} viewBox="0 0 20 20" fill="currentColor" data-id="element-134">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-135" />
        </svg>)}
    </div>;
}
function CardContent({
  card
}: {
  card: ScenarioCard;
}) {
  if (card.type === 'google-5star' || card.type === 'google-1star') {
    const colors = ['#4285F4', '#EA4335', '#FBBC04', '#34A853', '#FF6D00', '#46BDC6'];
    const color = colors[(card.author || 'U').charCodeAt(0) % colors.length];
    return <div className="bg-white rounded-xl p-5 shadow-sm w-full" data-id="element-136">
        <div className="flex items-center gap-3 mb-3" data-id="element-137">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{
          backgroundColor: color
        }} data-id="element-138">
            {(card.author || 'U').charAt(0)}
          </div>
          <div data-id="element-139">
            <div className="font-semibold text-gray-900 text-sm" data-id="element-140">
              {card.author}
            </div>
            <div className="flex items-center gap-2" data-id="element-141">
              <GoogleStars rating={card.rating || 5} data-id="element-142" />
              <span className="text-gray-400 text-xs" data-id="element-143">· 1 week ago</span>
            </div>
          </div>
          <div className="ml-auto" data-id="element-144">
            <svg viewBox="0 0 24 24" className="w-5 h-5" data-id="element-145">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-146" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-147" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-148" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-149" />
            </svg>
          </div>
        </div>
        <p className="text-gray-700 text-sm leading-relaxed" data-id="element-150">{card.content}</p>
        <div className="flex items-center gap-1 mt-3 text-gray-400 text-xs" data-id="element-151">
          <ThumbsUpIcon className="w-3 h-3" data-id="element-152" />
          <span data-id="element-153">Helpful</span>
        </div>
      </div>;
  }
  if (card.type === 'imessage-good' || card.type === 'imessage-bad') {
    const isGood = card.type === 'imessage-good';
    return <div className="bg-[#1C1C1E] rounded-2xl p-4 w-full border border-zinc-700/50" data-id="element-154">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-700/50" data-id="element-155">
          <div className="flex items-center gap-2" data-id="element-156">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${isGood ? 'bg-green-600' : 'bg-red-600'}`} data-id="element-157">
              {(card.author || 'U').charAt(0)}
            </div>
            <div data-id="element-158">
              <div className="text-white text-sm font-semibold" data-id="element-159">
                {card.author}
              </div>
              <div className="text-gray-500 text-xs" data-id="element-160">{card.label}</div>
            </div>
          </div>
          <span className="text-gray-500 text-xs" data-id="element-161">{card.time}</span>
        </div>
        <div className="flex justify-end" data-id="element-162">
          <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-br-sm text-white text-sm leading-relaxed bg-[#0A84FF]" data-id="element-163">
            {card.content}
          </div>
        </div>
        <div className="text-right mt-1" data-id="element-164">
          <span className="text-gray-600 text-xs" data-id="element-165">Delivered</span>
        </div>
      </div>;
  }
  if (card.type === 'lead') {
    return <div className="bg-zinc-800 rounded-xl p-5 w-full border border-zinc-600/50" data-id="element-166">
        <div className="flex items-center gap-2 mb-3" data-id="element-167">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-168" />
          <span className="text-green-400 text-xs font-bold uppercase tracking-wider" data-id="element-169">
            {card.label}
          </span>
          <span className="text-gray-500 text-xs ml-auto" data-id="element-170">{card.time}</span>
        </div>
        <div className="flex items-start gap-3" data-id="element-171">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0" data-id="element-172">
            {(card.author || 'L').charAt(0)}
          </div>
          <div data-id="element-173">
            <div className="text-gray-400 text-xs mb-1" data-id="element-174">{card.author}</div>
            <p className="text-white text-sm leading-relaxed" data-id="element-175">{card.content}</p>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-zinc-800 rounded-xl p-5 w-full border border-zinc-600/50" data-id="element-176">
      <div className="flex items-center gap-2 mb-3" data-id="element-177">
        <span className="text-yellow-400 text-xs font-bold uppercase tracking-wider" data-id="element-178">
          {card.label}
        </span>
      </div>
      <p className="text-white text-base leading-relaxed" data-id="element-179">{card.content}</p>
    </div>;
}
export function SwipeTest({
  onComplete
}: SwipeTestProps) {
  const [started, setStarted] = useState(false);
  const [cards] = useState<ScenarioCard[]>(() => shuffle(ALL_SCENARIOS).slice(0, CARDS_PER_GAME));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [swipeResult, setSwipeResult] = useState<Decision | null>(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const startTimeRef = useRef<number>(0);
  const startXRef = useRef(0);
  const currentCard = cards[currentIndex];
  const isDone = currentIndex >= cards.length;
  useEffect(() => {
    if (isDone && started) {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      onComplete(correct, cards.length, elapsed);
    }
  }, [isDone, started]);
  const triggerSwipe = useCallback((decision: Decision) => {
    if (isAnimatingOut || !currentCard) return;
    const isCorrect = decision === currentCard.correctAnswer;
    setSwipeResult(decision);
    setIsAnimatingOut(true);
    if (isCorrect) setCorrect(c => c + 1);
    setTimeout(() => {
      setCurrentIndex(i => i + 1);
      setDragX(0);
      setSwipeResult(null);
      setIsAnimatingOut(false);
    }, 300);
  }, [isAnimatingOut, currentCard]);
  const onTouchStart = (e: React.TouchEvent) => {
    if (!started || isAnimatingOut) return;
    startXRef.current = e.touches[0].clientX;
    setIsDragging(true);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    setDragX(e.touches[0].clientX - startXRef.current);
  };
  const onTouchEnd = () => {
    setIsDragging(false);
    if (Math.abs(dragX) > 80) triggerSwipe(dragX < 0 ? 'keep' : 'toss');else setDragX(0);
  };
  const onMouseDown = (e: React.MouseEvent) => {
    if (!started || isAnimatingOut) return;
    startXRef.current = e.clientX;
    setIsDragging(true);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setDragX(e.clientX - startXRef.current);
  };
  const onMouseUp = () => {
    setIsDragging(false);
    if (Math.abs(dragX) > 80) triggerSwipe(dragX < 0 ? 'keep' : 'toss');else setDragX(0);
  };
  const rotation = dragX / 18;
  const keepOpacity = Math.max(0, Math.min(1, -dragX / 120));
  const tossOpacity = Math.max(0, Math.min(1, dragX / 120));
  const getCardTransform = () => {
    if (isAnimatingOut) {
      const dir = swipeResult === 'keep' ? -1 : 1;
      return `translateX(${dir * 500}px) rotate(${dir * 25}deg)`;
    }
    return `translateX(${dragX}px) rotate(${rotation}deg)`;
  };
  if (!started) {
    return <div className="h-full flex flex-col items-center justify-center text-center p-6" data-id="element-180">
        <div className="text-5xl mb-4" data-id="element-181">🃏</div>
        <h2 className="text-3xl font-mono font-bold text-cyan-400 mb-2" data-id="element-182">
          KEEP OR TOSS?
        </h2>
        <p className="text-gray-400 mb-6 max-w-sm text-sm" data-id="element-183">
          {CARDS_PER_GAME} business scenarios. Swipe{' '}
          <span className="text-red-400 font-bold" data-id="element-184">LEFT to KEEP</span>, swipe{' '}
          <span className="text-green-400 font-bold" data-id="element-185">RIGHT to TOSS</span>. Your
          time starts when you tap Begin.
        </p>
        <div className="flex gap-8 mb-8 text-sm" data-id="element-186">
          <div className="flex flex-col items-center gap-1" data-id="element-187">
            <div className="w-12 h-12 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center text-xl" data-id="element-188">
              👈
            </div>
            <span className="text-red-400 font-bold" data-id="element-189">KEEP</span>
          </div>
          <div className="flex flex-col items-center gap-1" data-id="element-190">
            <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center text-xl" data-id="element-191">
              👉
            </div>
            <span className="text-green-400 font-bold" data-id="element-192">TOSS</span>
          </div>
        </div>
        <button onClick={() => {
        setStarted(true);
        startTimeRef.current = Date.now();
      }} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold uppercase tracking-wider px-10 py-4 rounded-sm text-lg transition-colors active:scale-95" data-id="element-193">
          BEGIN — TIMER STARTS NOW
        </button>
      </div>;
  }
  return <div className="h-full flex flex-col w-full max-w-sm mx-auto select-none" data-id="element-194">
      <div className="flex justify-between items-center mb-4 px-2" data-id="element-195">
        <div className="text-sm font-mono text-gray-400" data-id="element-196">
          {Math.min(currentIndex + 1, cards.length)}/{cards.length}
        </div>
        <div className="text-sm font-mono text-cyan-400" data-id="element-197">{correct} ✓</div>
      </div>

      <div className="flex justify-between px-4 mb-2 text-xs font-bold uppercase tracking-wider" data-id="element-198">
        <span className="text-red-400" data-id="element-199">← KEEP</span>
        <span className="text-green-400" data-id="element-200">TOSS →</span>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden" data-id="element-201">
        {isDone ? <div className="text-center" data-id="element-202">
            <div className="text-4xl mb-2" data-id="element-203">✅</div>
            <p className="text-white font-mono text-xl" data-id="element-204">Calculating...</p>
          </div> : <div className="w-full cursor-grab active:cursor-grabbing" style={{
        transform: getCardTransform(),
        transition: isDragging ? 'none' : isAnimatingOut ? 'transform 0.3s ease-out' : 'transform 0.15s ease-out'
      }} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} data-id="element-205">
            <div className="absolute inset-0 rounded-xl bg-red-500/20 border-4 border-red-500 z-10 flex items-center justify-center pointer-events-none" style={{
          opacity: keepOpacity,
          transition: isDragging ? 'none' : 'opacity 0.15s'
        }} data-id="element-206">
              <span className="text-red-400 text-4xl font-black rotate-[-15deg] border-4 border-red-400 px-4 py-1 rounded" data-id="element-207">
                KEEP
              </span>
            </div>
            <div className="absolute inset-0 rounded-xl bg-green-500/20 border-4 border-green-500 z-10 flex items-center justify-center pointer-events-none" style={{
          opacity: tossOpacity,
          transition: isDragging ? 'none' : 'opacity 0.15s'
        }} data-id="element-208">
              <span className="text-green-400 text-4xl font-black rotate-[15deg] border-4 border-green-400 px-4 py-1 rounded" data-id="element-209">
                TOSS
              </span>
            </div>
            <CardContent card={currentCard} data-id="element-210" />
          </div>}
        {!isDone && currentIndex + 1 < cards.length && <div className="absolute inset-0 -z-10 scale-95 opacity-40 pointer-events-none" data-id="element-211">
            <CardContent card={cards[currentIndex + 1]} data-id="element-212" />
          </div>}
      </div>

      <div className="flex gap-3 mt-4 px-2 pb-2" data-id="element-213">
        <button onClick={() => triggerSwipe('keep')} disabled={isAnimatingOut || isDone} className="flex-1 py-4 rounded-lg bg-red-500/20 border-2 border-red-500 text-red-400 font-black uppercase tracking-wider text-lg active:scale-95 transition-transform disabled:opacity-40" data-id="element-214">
          👈 KEEP
        </button>
        <button onClick={() => triggerSwipe('toss')} disabled={isAnimatingOut || isDone} className="flex-1 py-4 rounded-lg bg-green-500/20 border-2 border-green-500 text-green-400 font-black uppercase tracking-wider text-lg active:scale-95 transition-transform disabled:opacity-40" data-id="element-215">
          TOSS 👉
        </button>
      </div>
    </div>;
}